import { useEffect, useRef, useState } from "react";

import { useQueryClient } from "@tanstack/react-query";
import useOrders from "api/useOrders";
import { OrderDetails } from "common/contracts";
import { EOrderStatus, EQueryKeys } from "constants/enums";
import { EToasterStatus, showToast } from "utils/showToast";

import Button from 'components/Button/Button';
import { Modal } from 'components/Modal';

const POLLING_INTERVAL = 3000;
const MAX_POLLING_ATTEMPTS = 20; // 20 × 3s = 60 seconds max

export const RefundModal = ({ isOpen, onClose, currencySymbol, orderId, totalAmount, publisherId }: { isOpen: boolean, onClose: () => void, currencySymbol: string, orderId: string, totalAmount: string, publisherId: string }) => {
  const queryClient = useQueryClient();
  const { makeRefund } = useOrders(orderId, undefined, undefined, { selectedProjects: publisherId });
  const [isPolling, setIsPolling] = useState(false);
  const pollingAttemptsRef = useRef(0);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, []);

  const stopPolling = () => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
    setIsPolling(false);
    pollingAttemptsRef.current = 0;
  };

  const startPolling = () => {
    setIsPolling(true);
    pollingAttemptsRef.current = 0;

    intervalRef.current = setInterval(async () => {
      pollingAttemptsRef.current += 1;

      await queryClient.refetchQueries({
        queryKey: [EQueryKeys.ORDER_DETAILS, orderId]
      });

      const orderData = queryClient.getQueryData<OrderDetails>([EQueryKeys.ORDER_DETAILS, orderId]);
      const isRefunded = orderData?.status === EOrderStatus.payment_refunded;

      if (isRefunded || pollingAttemptsRef.current >= MAX_POLLING_ATTEMPTS) {
        stopPolling();
        if (isRefunded) {
          showToast({
            message: 'Refund successful',
            description: 'The refund has been processed',
            status: EToasterStatus.SUCCESS
          });
        }

        onClose();
      }
    }, POLLING_INTERVAL);
  };

  const handleRefund = () => {
    makeRefund.mutate(
      { orderId, publisherId },
      {
        onSuccess: () => {
          startPolling();
        }
      }
    );
  };

  if (isPolling) {
    return (
      <Modal isOpen={isOpen} onClose={() => { }} width={300} height={200}>
        <div className="flex flex-col items-center justify-center gap-4 py-6">
          <div className="w-8 h-8 border-4 border-base-primary border-t-transparent rounded-full animate-spin" />
          <div className="text-center">
            <div className="font-semibold text-base-primary">Processing refund...</div>
            <div className="text-xs text-base-mutedForeground mt-2">
              Please wait while we confirm the refund status
            </div>
          </div>
        </div>
      </Modal>
    );
  }

  const isProcessing = makeRefund.isLoading;

  return (
    <Modal isOpen={isOpen} onClose={isProcessing ? () => { } : onClose} width={300} height={260}>
      <div className="flex flex-col gap-4 mt-2">
        <div className="font-semibold text-base-primary">Refund payment</div>
        <div className="text-base-secondary">
          <div className="text-xs text-base-mutedForeground pb-4 border-b border-base-border">
            Refunds take 5-10 days to appear on a customer's statement.
          </div>
          <div className="flex flex-col gap-1 mt-4">
            <div className="text-xs text-base-mutedForeground">
              Refund amount:
            </div>
            <div className="text-base-primary">
              {currencySymbol}
              {totalAmount}
            </div>
          </div>

          <div className="flex justify-end gap-2 mt-2">
            <Button
              variant="text"
              onClick={onClose}
              disabled={isProcessing}
              data-testid="cancelButtonDialog"
            >
              Cancel
            </Button>
            <Button
              onClick={handleRefund}
              disabled={isProcessing}
              data-testid="refundButtonDialog"
            >
              {isProcessing ? 'Refunding...' : 'Refund'}
            </Button>
          </div>
        </div>
      </div>
    </Modal>
  );
};
